// resources/js/Pages/HallOfFame/components/EmotionChart.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';

export default function EmotionChart({ emotionsData, emotionSummaries, allScripts }) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [currentEmotion, setCurrentEmotion] = useState(null);
    
    const colors = {
        "suspense": "#FF4500",
        "empathy": "#2E8B57",
        "fear": "#DC143C",
        "surprise": "#FFD700",
        "joy": "#1E90FF",
        "sadness": "#708090"
    };
    
    // Calculate emotion percentiles
    const emotionPercentiles = useMemo(() => {
        if (!emotionsData || !allScripts) {
            console.log('Missing data:', { emotionsData, allScripts: !!allScripts });
            return null;
        }
        
        console.log('Calculating percentiles for emotions:', emotionsData);
        
        const percentiles = {};
        
        Object.keys(emotionsData).forEach(emotion => {
            const value = emotionsData[emotion];
            
            // Collect all values for this emotion across all scripts
            const allValues = allScripts
                .map(s => {
                    // Handle both parsed objects and JSON strings
                    const emotions = typeof s.emotions === 'string' 
                        ? JSON.parse(s.emotions) 
                        : s.emotions;
                    return emotions?.[emotion];
                })
                .filter(v => v !== null && v !== undefined && v > 0);
            
            if (allValues.length === 0) {
                percentiles[emotion] = 0;
                return;
            }
            
            const countLess = allValues.filter(v => v < value).length;
            percentiles[emotion] = Math.round((countLess / allValues.length) * 100);
        });
        
        console.log('Calculated percentiles:', percentiles);
        return percentiles;
    }, [emotionsData, allScripts]);
    
    useEffect(() => {
        if (!emotionPercentiles || !canvasRef.current || !containerRef.current) {
            console.log('Missing refs or percentiles:', { 
                emotionPercentiles, 
                canvas: !!canvasRef.current, 
                container: !!containerRef.current 
            });
            return;
        }
        
        const canvas = canvasRef.current;
        const container = containerRef.current;
        const ctx = canvas.getContext('2d');
        
        const emotions = Object.keys(emotionPercentiles);
        const sliceAngle = (2 * Math.PI) / emotions.length;
        const maxPercentileValue = 100;
        const lineColor = '#fff';
        const minSize = 200;
        
        const resizeCanvas = () => {
            const containerRect = container.getBoundingClientRect();
            const size = Math.max(minSize, containerRect.width);
            canvas.width = size;
            canvas.height = size;
            canvas.style.width = `${size}px`;
            canvas.style.height = `${size}px`;
            drawGraph();
        };
        
        const drawGraph = () => {
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            const maxRadius = Math.min(canvas.width, canvas.height) / 2.5;
            
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Draw outer circle
            ctx.beginPath();
            ctx.arc(centerX, centerY, maxRadius, 0, 2 * Math.PI);
            ctx.strokeStyle = lineColor;
            ctx.lineWidth = 2;
            ctx.stroke();
            ctx.closePath();
            
            // Draw emotion slices
            emotions.forEach((emotion, index) => {
                const intensity = emotionPercentiles[emotion];
                const color = colors[emotion] || '#999';
                
                const startAngle = index * sliceAngle;
                const endAngle = startAngle + sliceAngle;
                const sliceRadius = (intensity / maxPercentileValue) * maxRadius;
                
                // Draw slice
                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                ctx.arc(centerX, centerY, sliceRadius, startAngle, endAngle);
                ctx.closePath();
                ctx.fillStyle = color;
                ctx.fill();
                
                // Draw border
                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                const endX = centerX + maxRadius * Math.cos(startAngle);
                const endY = centerY + maxRadius * Math.sin(startAngle);
                ctx.lineTo(endX, endY);
                ctx.strokeStyle = lineColor;
                ctx.stroke();
                ctx.closePath();
                
                // Draw label
                const midAngle = (startAngle + endAngle) / 2;
                let labelRadius = maxRadius + 30;
                
                if (Math.abs(Math.sin(midAngle)) > 0.7) {
                    labelRadius = maxRadius + 15;
                }
                
                const labelX = centerX + labelRadius * Math.cos(midAngle);
                const labelY = centerY + labelRadius * Math.sin(midAngle);
                
                ctx.fillStyle = lineColor;
                ctx.font = "14px Arial";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                
                let yOffset = 0;
                if (Math.sin(midAngle) < -0.7) yOffset = -5;
                if (Math.sin(midAngle) > 0.7) yOffset = 5;
                
                ctx.fillText(emotion, labelX, labelY + yOffset);
            });
            
            console.log('Drew graph with emotions:', emotions);
        };
        
        const handleMouseMove = (event) => {
            const rect = canvas.getBoundingClientRect();
            const mouseX = event.clientX - rect.left;
            const mouseY = event.clientY - rect.top;
            
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            
            let hoveredEmotion = null;
            
            emotions.forEach((emotion, index) => {
                const startAngle = index * sliceAngle;
                const endAngle = startAngle + sliceAngle;
                
                const dx = mouseX - centerX;
                const dy = mouseY - centerY;
                const angle = Math.atan2(dy, dx);
                const adjustedAngle = angle >= 0 ? angle : 2 * Math.PI + angle;
                
                if (adjustedAngle >= startAngle && adjustedAngle <= endAngle) {
                    hoveredEmotion = emotion;
                }
            });
            
            setCurrentEmotion(hoveredEmotion);
        };
        
        const handleMouseOut = () => {
            setCurrentEmotion(null);
        };
        
        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseout', handleMouseOut);
        
        const resizeObserver = new ResizeObserver(resizeCanvas);
        resizeObserver.observe(container);
        
        resizeCanvas();
        
        return () => {
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseout', handleMouseOut);
            resizeObserver.disconnect();
        };
    }, [emotionPercentiles]);
    
    if (!emotionsData) {
        return <p className="text-white text-center">No emotion data available</p>;
    }
    
    if (!emotionPercentiles) {
        return <p className="text-white text-center">Calculating percentiles...</p>;
    }
    
    return (
        <div ref={containerRef} className="graph_info position-relative emotion-graph" style={{ aspectRatio: '1/1', width: '100%' }}>
            <canvas ref={canvasRef}></canvas>
            {currentEmotion && (
                <div 
                    className="emotion-tooltip"
                    style={{ display: 'block' }}
                >
                    <strong>{currentEmotion}</strong>
                    <div className="intensity">
                        Intensity: {emotionPercentiles[currentEmotion].toFixed(1)}%
                    </div>
                    {emotionSummaries?.[currentEmotion] && (
                        <div className="description">
                            {emotionSummaries[currentEmotion]}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}